import { Chip, CircularProgress, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@material-ui/core";
import { ExpandLess, ExpandMore } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { EntryHistoryItem } from "../../../model/EntryHistoryItem";
import { ApiBackend } from "../../../providers/apibackend";
import { vasaloppetDateTimeGetterFormatter } from "../../../utilities/date";

interface IProps {
    entryId: string;
}

// Fält som spåras i diffen mellan på varandra följande ögonblicksbilder
const TRACKED: { key: string; label: string }[] = [
    { key: "firstName", label: "Förnamn" },
    { key: "lastName", label: "Efternamn" },
    { key: "personId", label: "PersonId" },
    { key: "email", label: "E-post" },
    { key: "phone", label: "Telefon" },
    { key: "status", label: "Status" },
    { key: "raceStatus", label: "Race-status" },
    { key: "bibtext", label: "Nummerlapp" },
    { key: "startgroup", label: "Startgrupp" },
    { key: "clubName", label: "Klubb" },
    { key: "team", label: "Lag" },
    { key: "dsq", label: "Diskvalificerad" },
    { key: "sex", label: "Kön" },
    { key: "nationality", label: "Nationalitet" },
    { key: "dateOfBirthYear", label: "Födelseår" },
    { key: "orderId", label: "OrderId" },
    { key: "orderItemId", label: "OrderItemId" }
];

const fmt = (value: any): string => {
    if (value === null || value === undefined || value === "") {
        return "(tomt)";
    }
    if (typeof value === "boolean") {
        return value ? "Ja" : "Nej";
    }
    return String(value);
};

interface IChange {
    key: string;
    label: string;
    from: any;
    to: any;
}

const diff = (prev: any, cur: any): IChange[] => {
    const changes: IChange[] = [];
    for (const field of TRACKED) {
        const a = prev ? prev[field.key] : undefined;
        const b = cur ? cur[field.key] : undefined;
        if (fmt(a) !== fmt(b)) {
            changes.push({ key: field.key, label: field.label, from: a, to: b });
        }
    }
    return changes;
};

const actionColor = (action: string): string => {
    if (action === "INSERT") {
        return "#e0f2e9";
    }
    if (action === "DELETE") {
        return "#fdecea";
    }
    return "#e8eef5";
};

const EntryHistoryCard = ({ entryId }: IProps) => {
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState<EntryHistoryItem[]>([]);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        new ApiBackend().getEntryHistory(entryId).then((res) => {
            if (mounted) {
                setHistory(res ?? []);
                setLoading(false);
            }
        });
        return () => { mounted = false; };
    }, [entryId]);

    if (loading) {
        return <div style={{ padding: 24, textAlign: "center" }}><CircularProgress color="secondary" /></div>;
    }

    if (!history.length) {
        return <Typography variant="body2" color="textSecondary" style={{ padding: 8 }}>Ingen historik hittades för denna anmälan.</Typography>;
    }

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell style={{ whiteSpace: "nowrap" }}>Tidpunkt</TableCell>
                    <TableCell>Händelse</TableCell>
                    <TableCell>Källa</TableCell>
                    <TableCell>Ändringar</TableCell>
                    <TableCell />
                </TableRow>
            </TableHead>
            <TableBody>
                {history.map((item, idx) => {
                    const prev = idx > 0 ? history[idx - 1].payload : null;
                    const isInsert = item.action === "INSERT" || idx === 0;
                    const changes = isInsert ? [] : diff(prev, item.payload);
                    const isOpen = expandedId === item.id;

                    return (
                        <React.Fragment key={item.id}>
                            <TableRow>
                                <TableCell style={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                                    {vasaloppetDateTimeGetterFormatter(item.created)}
                                </TableCell>
                                <TableCell style={{ verticalAlign: "top" }}>
                                    <Chip size="small" label={item.action} style={{ background: actionColor(item.action) }} />
                                </TableCell>
                                <TableCell style={{ verticalAlign: "top" }}>{item.source || "-"}</TableCell>
                                <TableCell style={{ verticalAlign: "top" }}>
                                    {isInsert ? (
                                        <Typography variant="body2">
                                            Anmälan skapad{item.payload ? ` – ${fmt(item.payload.firstName)} ${fmt(item.payload.lastName)}` : ""}
                                        </Typography>
                                    ) : changes.length === 0 ? (
                                        <Typography variant="body2" color="textSecondary">Inga spårade fält ändrade</Typography>
                                    ) : (
                                        changes.map((c) => (
                                            <div key={c.key} style={{ fontSize: 13, lineHeight: 1.6 }}>
                                                <b style={{ color: c.key === "personId" ? "#c62828" : undefined }}>{c.label}:</b>{" "}
                                                <span style={{ textDecoration: "line-through", color: "#9e9e9e" }}>{fmt(c.from)}</span>
                                                {" → "}
                                                <span>{fmt(c.to)}</span>
                                            </div>
                                        ))
                                    )}
                                </TableCell>
                                <TableCell style={{ verticalAlign: "top" }}>
                                    <IconButton size="small" title="Visa rådata" onClick={() => setExpandedId(isOpen ? null : item.id)}>
                                        {isOpen ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                            {isOpen &&
                                <TableRow>
                                    <TableCell colSpan={5} style={{ background: "#fafafa" }}>
                                        <pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                                            {JSON.stringify(item.payload, null, 2)}
                                        </pre>
                                    </TableCell>
                                </TableRow>
                            }
                        </React.Fragment>
                    );
                })}
            </TableBody>
        </Table>
    );
};

export default EntryHistoryCard;
